import type { CreateListingDataInput, SuggestListingRolesInput } from "@/graphql/generated/operations";
import type { DashboardFacetKey } from "@/pages/project/dashboard-kinds";
import { createListingData, EMPTY_LISTING_FORM, ValidationRejected } from "@/pages/share/listings/listing-form";
import { listingRequirementsData, listingRequirementsDesign } from "@/pages/share/listings/listing-requirements";
import { MatchCondition, type Composition } from "./design-codec";
import { compareGaps, isCriterionFulfilled, type RequirementAnalysis } from "./match-result-derive";

// "Aus Lücken Stellen planen" (spark#1237): offene Anforderungen eines Team-Ergebnisses werden zu
// Ausschreibungsentwürfen. Rein, damit Auswahl, Rollen und die Übersetzung in createListing ohne
// Oberfläche prüfbar sind. Keine eigene Matching-Logik: die Lücken sind buildRequirementAnalysis,
// die Anforderungen sind das Listing-Format, das ListingRequirements liest und schreibt.

/**
 * Wie belastbar eine Lücke ist. Nur DEFICIT ist ein nachgewiesenes Defizit; gewählt wird keine
 * Lücke von selbst. UNVERIFIED wäre erfüllt, wenn die unbewerteten Mitglieder sie trügen (UNKNOWN,
 * spark#175) und wird als nicht nachgewiesen gekennzeichnet.
 * OVERSHOOT ist eine Obergrenze, die zu viele Träger reißen: eine weitere Person schließt sie nie.
 */
export type GapEvidence = "DEFICIT" | "UNVERIFIED" | "OVERSHOOT";

export interface GapRequirement {
  /** Schlüssel innerhalb des Ergebnisteams; das Team wechselt, die Schlüssel mit. */
  key: string;
  analysis: RequirementAnalysis;
  /** Als Stellenanforderung: Muss/Kann und Niveau wie im Match, ohne Team-Auflage. */
  condition: MatchCondition;
  evidence: GapEvidence;
}

// Dieselbe Auflage, die ein neu angelegtes Kriterium im RANK-Fall bekommt (newCondition). Eine
// Ausschreibung bewertet Bewerbende einzeln; "mind. 2 im Team" sagt dort nichts. Die
// Listing-Anforderungsdifferenz ignoriert die Auflage aus demselben Grund.
const LISTING_COMPOSITION: Composition = { operator: "ALL", value: null };

function evidenceOf(a: RequirementAnalysis): GapEvidence {
  if (a.operator === "MAX") return "OVERSHOOT";
  if (a.operator === "EXACT" && a.required != null && a.met > a.required) return "OVERSHOOT";
  // Nicht nachgewiesen, sobald IRGENDEINE Zahl der Unbewerteten die Auflage erfüllen könnte: bei
  // EXACT 1 mit zwei Unbewerteten genügt einer, alle zusammen wären schon zu viel.
  for (let k = 1; k <= a.unrated; k++) {
    if (isCriterionFulfilled(a.condition, a.met + k, a.teamSize)) return "UNVERIFIED";
  }
  return "DEFICIT";
}

function listingCondition(a: RequirementAnalysis): MatchCondition | null {
  if (!a.condition || typeof a.condition !== "object") return null;
  // active und composition sind im Codec Pflicht; ein Ergebnis-Kriterium war aktiv, sonst stünde es
  // nicht im Ergebnis.
  const parsed = MatchCondition.safeParse({ active: true, composition: LISTING_COMPOSITION, ...a.condition });
  if (!parsed.success) return null;
  return {
    ...parsed.data,
    active: true,
    composition: LISTING_COMPOSITION,
    requirement: a.isMust ? "MUST" : "OPTIONAL",
  };
}

/** Die Lücken des Teams in der Reihenfolge der Match-Analyse; Kriterien ohne Listing-Form fallen weg. */
export function buildGapRequirements(analysis: readonly RequirementAnalysis[]): GapRequirement[] {
  return analysis
    .filter((a) => !a.fulfilled)
    .sort(compareGaps)
    .flatMap((a) => {
      const condition = listingCondition(a);
      return condition ? [{ key: `c${a.index}`, analysis: a, condition, evidence: evidenceOf(a) }] : [];
    });
}

/** Ob die Aktion überhaupt etwas anzubieten hat. */
export function hasPlannableGaps(gaps: readonly GapRequirement[]): boolean {
  return gaps.some((g) => g.evidence !== "OVERSHOOT");
}

export interface PlannedRole {
  id: string;
  title: string;
  keys: string[];
}

/**
 * `selected` ist, was in die Entwürfe soll. Gewählt wird durch Zuordnen, die letzte Abwahl hebt die
 * Auswahl auf. Gewählt und doch ohne Rolle ist ein Kriterium nur, wenn ein KI-Vorschlag es ausließ:
 * dann bleibt es sichtbar und blockiert, statt still zu verschwinden.
 */
export interface GapPlan {
  selected: string[];
  roles: PlannedRole[];
}

/** Leer: gewählt wird nur, was der Nutzer selbst zuordnet. */
export function initialGapPlan(newId: () => string): GapPlan {
  return { selected: [], roles: [{ id: newId(), title: "", keys: [] }] };
}

export function rolesOf(plan: GapPlan, key: string): PlannedRole[] {
  return plan.roles.filter((r) => r.keys.includes(key));
}

/** Ordnet ein Kriterium einer Rolle zu oder nimmt es dort weg. */
export function toggleRoleAssignment(plan: GapPlan, roleId: string, gap: GapRequirement): GapPlan {
  if (gap.evidence === "OVERSHOOT") return plan;
  const roles = plan.roles.map((r) => r.id !== roleId ? r
    : { ...r, keys: r.keys.includes(gap.key) ? r.keys.filter((k) => k !== gap.key) : [...r.keys, gap.key] });
  const assigned = roles.some((r) => r.keys.includes(gap.key));
  const selected = assigned
    ? (plan.selected.includes(gap.key) ? plan.selected : [...plan.selected, gap.key])
    : plan.selected.filter((k) => k !== gap.key);
  return { selected, roles };
}

export function renameRole(plan: GapPlan, roleId: string, title: string): GapPlan {
  return { ...plan, roles: plan.roles.map((r) => (r.id === roleId ? { ...r, title } : r)) };
}

export function addRole(plan: GapPlan, newId: () => string): GapPlan {
  return { ...plan, roles: [...plan.roles, { id: newId(), title: "", keys: [] }] };
}

/** Entfernt eine Rolle; was nur ihr zugeordnet war, ist danach nicht mehr gewählt. */
export function removeRole(plan: GapPlan, roleId: string): GapPlan {
  const removed = plan.roles.find((r) => r.id === roleId);
  if (!removed || plan.roles.length <= 1) return plan;
  const roles = plan.roles.filter((r) => r.id !== roleId);
  const orphaned = removed.keys.filter((k) => !roles.some((r) => r.keys.includes(k)));
  return { selected: plan.selected.filter((k) => !orphaned.includes(k)), roles };
}

/** Was aus einer Rolle tatsächlich in den Entwurf geht: zugeordnet UND gewählt. */
export function roleKeys(plan: GapPlan, role: PlannedRole): string[] {
  return role.keys.filter((k) => plan.selected.includes(k));
}

export function unassignedKeys(plan: GapPlan): string[] {
  return plan.selected.filter((k) => !plan.roles.some((r) => r.keys.includes(k)));
}

export type RoleIssue = "TITLE" | "EMPTY";

export function roleIssues(plan: GapPlan, role: PlannedRole): RoleIssue[] {
  const issues: RoleIssue[] = [];
  if (!role.title.trim()) issues.push("TITLE");
  if (roleKeys(plan, role).length === 0) issues.push("EMPTY");
  return issues;
}

/**
 * Ob die offenen Rollen angelegt werden können. Eine gewählte Lücke ohne Rolle blockiert: still
 * weglassen hieße, dass eine Auswahl des Nutzers nicht ankommt. Zuordnen oder abwählen.
 */
export function rolesReady(plan: GapPlan, roles: readonly PlannedRole[]): boolean {
  return roles.length > 0 && unassignedKeys(plan).length === 0
    && roles.every((r) => roleIssues(plan, r).length === 0);
}

function normalTitle(title: string): string {
  return title.trim().toLocaleLowerCase();
}

export interface RoleSuggestion {
  title: string;
  requirementKeys: readonly string[];
}

/**
 * Übernimmt einen KI-Vorschlag in den Plan. Genesis filtert schon auf die gesendeten Schlüssel; hier
 * noch einmal auf die GEWÄHLTEN, weil sich die Auswahl während der Anfrage geändert haben kann.
 * Eine benannte Rolle des Nutzers behält ihre Id, wenn der Vorschlag ihren Titel trägt; eine, die er
 * nicht aufgreift, bleibt mit Titel stehen (ohne Zuordnung), damit keine Eingabe verloren geht. Die
 * Auswahl selbst ändert der Vorschlag nie.
 */
export function applyRoleSuggestion(plan: GapPlan, suggestion: readonly RoleSuggestion[], newId: () => string): GapPlan {
  const selected = new Set(plan.selected);
  const unclaimed = [...plan.roles];
  const roles: PlannedRole[] = [];
  for (const s of suggestion) {
    const title = s.title.trim();
    const keys = [...new Set(s.requirementKeys.filter((k) => selected.has(k)))];
    if (!title || keys.length === 0) continue;
    const match = unclaimed.findIndex((r) => r.title.trim() && normalTitle(r.title) === normalTitle(title));
    const id = match >= 0 ? unclaimed.splice(match, 1)[0]!.id : newId();
    roles.push({ id, title, keys });
  }
  if (roles.length === 0) return plan;
  for (const r of unclaimed) if (r.title.trim()) roles.push({ ...r, keys: [] });
  return { ...plan, roles };
}

/** Grenzen von genesis `suggestListingRoles`. */
export const SUGGESTION_MAX_REQUIREMENTS = 50;
export const SUGGESTION_MAX_ROLE_TITLES = 10;
export const SUGGESTION_MAX_HINT = 1_000;

export function roleSuggestionInput({ plan, gaps, labelOf, matchId, locale, hint }: {
  plan: GapPlan;
  gaps: readonly GapRequirement[];
  labelOf: (gap: GapRequirement) => string;
  matchId: string;
  locale: string;
  hint: string;
}): SuggestListingRolesInput | null {
  const chosen = gaps.filter((g) => plan.selected.includes(g.key));
  if (chosen.length === 0 || chosen.length > SUGGESTION_MAX_REQUIREMENTS) return null;
  const titles = [...new Set(plan.roles.map((r) => r.title.trim()).filter(Boolean))].slice(0, SUGGESTION_MAX_ROLE_TITLES);
  return {
    matchId,
    locale,
    requirements: chosen.map((g) => ({ key: g.key, label: labelOf(g).slice(0, 300), must: g.analysis.isMust })),
    roleTitles: titles,
    hint: hint.trim().slice(0, SUGGESTION_MAX_HINT) || null,
  };
}

/** Die Anforderungen eines neuen Entwurfs: der Stand einer frisch angelegten Ausschreibung plus die Kriterien. */
export function listingMatchRequirements(conditions: readonly MatchCondition[]): Record<string, unknown> {
  const base = listingRequirementsData({ searchTarget: "PEOPLE" });
  // Der globale KI-Schalter des Listings wird beim Lesen aus den aiModes abgeleitet. Übernommen wird,
  // was die übernommenen Kriterien selbst sagen; ohne KI-fähiges Kriterium bleibt der Standard.
  const aiModes = conditions.flatMap((c) => ("aiMode" in c && c.aiMode ? [c.aiMode] : []));
  const aiEnabled = aiModes.length ? aiModes.includes("FULL_AI") : base.aiEnabled;
  return listingRequirementsDesign({ ...base, conditions: [...conditions], aiEnabled });
}

/**
 * Die Fläche, in der die übernommenen Anforderungen entstanden sind. Derselbe gespeicherte Bestand,
 * den das Dashboard mit keepFacets schreibt (`data.sections`; `team` heißt im UI "Anforderungen"):
 * ohne ihn öffnet der Entwurf mit "+ Anforderungen hinzufügen", obwohl er Anforderungen trägt.
 */
const REQUIREMENTS_FACETS: DashboardFacetKey[] = ["team"];

/**
 * Der createListing-Eingang einer Rolle. Die Beschreibung ist die fachliche Beschreibung des
 * Matches, wörtlich, nie seine internen Notizen; Personen, Werte und Begründungen aus der Analyse
 * gehen nicht hinaus. Ohne Leserschaft, nicht öffentlich: genesis legt einen Entwurf an.
 */
export function listingDraftInput(plan: GapPlan, role: PlannedRole, gaps: readonly GapRequirement[],
  description: string): CreateListingDataInput {
  const keys = roleKeys(plan, role);
  const conditions = gaps.filter((g) => keys.includes(g.key)).map((g) => g.condition);
  return {
    ...createListingData({ ...EMPTY_LISTING_FORM, name: role.title }),
    // Nach dem Helfer gesetzt, der trimmt: die Beschreibung geht unverändert hinaus.
    description: description.trim() ? description : null,
    data: { searchTarget: "PEOPLE", sections: REQUIREMENTS_FACETS, matchRequirements: listingMatchRequirements(conditions) },
  };
}

export type RoleCreation =
  | { state: "CREATED"; listingId: string }
  | { state: "UNCERTAIN" }
  | { state: "FAILED"; message: string };

/**
 * Sicher "nichts angelegt" heißt nur die strukturierte Ablehnung von genesis (ValidateListingResult).
 * Alles andere lässt offen, ob der Entwurf entstand: Zeitüberschreitung, Abbruch, Fehlerstatus vom
 * Proxy, aber auch ein GraphQL-Fehler, der erst beim Auflösen des Ergebnisses nach dem Schreiben
 * entstehen kann. Den schickt die Ansicht nicht noch einmal, sie verweist auf die Verwaltung.
 */
export function creationFailureIsCertain(error: unknown): boolean {
  return error instanceof ValidationRejected;
}

/** Rollen, die (noch einmal) angelegt werden dürfen: nie angefangen oder sicher gescheitert. */
export function pendingRoles(plan: GapPlan, creations: Readonly<Record<string, RoleCreation>>): PlannedRole[] {
  return plan.roles.filter((r) => !creations[r.id] || creations[r.id]!.state === "FAILED");
}

/**
 * Wann der Plan neu beginnt: anderes Ergebnis oder anderer Anforderungsstand. Die Schlüssel allein
 * (c0, c1, ...) reichen nicht, zwei Ergebnisse desselben Teams können an derselben Stelle ein anderes
 * Zielniveau tragen.
 */
export function gapPlanIdentity(resultId: string | null, gaps: readonly GapRequirement[]): string {
  return JSON.stringify([resultId, gaps.map((g) => [g.key, g.evidence, g.condition])]);
}
